import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Button, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { PortalService } from "services/portal";

// Versión del worker — debe coincidir con la dep instalada (pdfjs-dist 3.x).
const PDF_WORKER_URL = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

/**
 * Modal con visor PDF embebido. Pide un SAS URL al backend cuando se
 * abre. La URL nunca se muestra al usuario; solo se pasa al Viewer y
 * al botón Descargar.
 */
export function PdfViewerModal({ open, dte, onClose }) {
  const [viewUrl, setViewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const layoutPlugin = defaultLayoutPlugin();

  const loadViewUrl = useCallback(async () => {
    if (!dte) return;
    setLoading(true);
    setError(null);
    try {
      const res = await PortalService.getDteViewUrl(dte._id);
      setViewUrl(res.url);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "No se pudo cargar el PDF");
    } finally {
      setLoading(false);
    }
  }, [dte]);

  useEffect(() => {
    if (open && dte) {
      setViewUrl(null);
      loadViewUrl();
    } else if (!open) {
      setViewUrl(null);
      setError(null);
    }
  }, [open, dte, loadViewUrl]);

  const handleDownload = async () => {
    if (!dte) return;
    setDownloading(true);
    try {
      const res = await PortalService.getDteDownloadUrl(dte._id);
      // Disparo de descarga sin exponer la URL en la barra del navegador.
      const a = document.createElement("a");
      a.href = res.url;
      a.download = res.filename || `${dte.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "No se pudo descargar");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          {dte ? (
            <>
              {dte.direction === "EMITIDAS" ? "Factura emitida" : "Factura recibida"}
              <span style={{ marginLeft: 12, fontFamily: "monospace", color: "#64748b", fontSize: 12 }}>
                {dte.code}
              </span>
            </>
          ) : (
            "Documento"
          )}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Button
            startIcon={<FileDownloadIcon />}
            size="small"
            onClick={handleDownload}
            disabled={downloading || !dte?.file?.blobName}
          >
            {downloading ? "Preparando..." : "Descargar"}
          </Button>
          <IconButton onClick={onClose} size="small" aria-label="Cerrar"><CloseIcon /></IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: "80vh" }}>
        {loading && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <CircularProgress size={32} />
          </div>
        )}
        {error && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, padding: 24 }}>
            <p style={{ color: "#991b1b", fontSize: 14, textAlign: "center" }}>{error}</p>
            <Button startIcon={<RefreshIcon />} onClick={loadViewUrl} variant="outlined" size="small">
              Reintentar
            </Button>
          </div>
        )}
        {viewUrl && !loading && !error && (
          <Worker workerUrl={PDF_WORKER_URL}>
            <Viewer fileUrl={viewUrl} plugins={[layoutPlugin]} />
          </Worker>
        )}
      </DialogContent>
    </Dialog>
  );
}
